import React, { useEffect, useMemo, useState } from "react";
import { Button, Avatar, Card, Checkbox, Col, DatePicker, Form, Input, Modal, Row, Select, Space, Tabs, Tag, Typography, message, Divider } from "antd";
import { LoadingOutlined, MailOutlined, PhoneOutlined, SyncOutlined, UserOutlined, SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import useView from "../../hooks/useView";

const { Text, Title } = Typography;
const { TextArea } = Input;

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
const hasSupabaseConfig = Boolean(supabaseUrl && supabaseAnonKey);
const supabaseRestUrl = hasSupabaseConfig ? `${supabaseUrl}/rest/v1` : null;

const behaviorOptions = [
  { label: "🗣 Asked great questions / Participated", value: "questions" },
  { label: "🤝 Helped a teammate", value: "helped" },
  { label: "👔 Excellent professional etiquette / Dress code", value: "etiquette" },
  { label: "📱 Distracted by Phone / Social Media", value: "distracted" },
  { label: "💤 Low Energy / Disengaged", value: "low_energy" },
  { label: "⏰ Late / Unprepared", value: "late" },
  { label: "🛡 Defensive / Argumentative", value: "defensive" },
];

const performanceOptions = [
  { label: "🟢 Exceed expectation", value: "exceed" },
  { label: "🟡 Meet expectation", value: "meet" },
  { label: "🔴 Below expectation", value: "below" },
];

const activityTypeOptions = [
  { label: "A.I. workshop", value: "ai" },
  { label: "Marketing workshop", value: "marketing" },
  { label: "Finance workshop", value: "finance" },
  { label: "Operations workshop", value: "operations" },
  { label: "Excursion", value: "excursion" },
  { label: "Visit", value: "visit" },
  { label: "Other", value: "other" },
];

const dayKeys = Array.from({ length: 10 }, (_, i) => i + 1);

const buildEmptyDays = () =>
  dayKeys.map(() => ({
    date: null,
    activity_type: undefined,
    behaviors: [],
    performance: undefined,
    notes: "",
  }));

const FeedbackDubai = () => {
  const [open, setOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [feedbackByUser, setFeedbackByUser] = useState({});
  const [form] = Form.useForm();
  const [activeDay, setActiveDay] = useState("1");
  const [loadingRemote, setLoadingRemote] = useState(false);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [teamFilter, setTeamFilter] = useState("");
  const { isMobile } = useView();

  useEffect(() => {
    const loadUsers = async () => {
      if (!hasSupabaseConfig || !supabaseRestUrl) {
        message.info("Missing imp configurations.");
        setUsers([]);
        return;
      }
      setLoadingUsers(true);
      try {
        const res = await fetch(`${supabaseRestUrl}/users?select=id,name,phone,email,image,team`, {
          headers: {
            apikey: supabaseAnonKey,
            Authorization: `Bearer ${supabaseAnonKey}`,
            "Content-Type": "application/json",
          },
        });
        if (!res.ok) throw new Error("Failed to load users");
        const data = await res.json();
        setUsers(data || []);
      } catch (err) {
        console.error(err);
        message.error("Could not load users from Supabase");
        setUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };
    loadUsers();
  }, []);

  const handleOpen = async (user) => {
    setSelectedUser(user);
    const existing = feedbackByUser[user.id] || buildEmptyDays();
    form.setFieldsValue({ days: existing });
    setActiveDay("1");
    setOpen(true);

    if (!hasSupabaseConfig || !supabaseRestUrl) {
      message.info("Supabase env vars missing; feedback will stay local.");
      return;
    }

    setLoadingRemote(true);
    try {
      const res = await fetch(
        `${supabaseRestUrl}/internship_feedback?user_id=eq.${encodeURIComponent(user.id)}&select=day,date,activity_type,behaviors,performance,notes&order=day`,
        {
          headers: {
            apikey: supabaseAnonKey,
            Authorization: `Bearer ${supabaseAnonKey}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!res.ok) throw new Error("Fetch failed");
      const data = await res.json();

      if (data?.length) {
        const hydrated = buildEmptyDays();
        data.forEach((row) => {
          const dayIndex = Number(row.day) - 1;
          if (dayIndex >= 0 && dayIndex < hydrated.length) {
            hydrated[dayIndex] = {
              date: row.date ? dayjs(row.date) : null,
              activity_type: row.activity_type || undefined,
              behaviors: row.behaviors || [],
              performance: row.performance || undefined,
              notes: row.notes || "",
            };
          }
        });
        setFeedbackByUser((prev) => ({ ...prev, [user.id]: hydrated }));
        form.setFieldsValue({ days: hydrated });
      }
    } catch (err) {
      console.error(err);
      message.error("Could not load feedback from Supabase");
    } finally {
      setLoadingRemote(false);
    }
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const handleSubmit = async () => {
    try {
      //   const values = await form.validateFields();
      const values = await form.getFieldsValue();
      if (!selectedUser) return;

      setFeedbackByUser((prev) => ({
        ...prev,
        [selectedUser.id]: values.days,
      }));

      if (!hasSupabaseConfig || !supabaseRestUrl) {
        message.info("Supabase env vars missing; saved locally only.");
        setOpen(false);
        return;
      }

      setSaving(true);

      const payload = [];
      values.days.map((day, idx) => {
        if (day && day.date) {
          payload.push({
            date: day.date ? dayjs(day.date).format("YYYY-MM-DD") : null,
            activity_type: day.activity_type || null,
            behaviors: day.behaviors || [],
            performance: day.performance || null,
            notes: day.notes || "",
            user_id: selectedUser.id,
            day: idx + 1,
          });
        }
      });

      console.log(values.days, payload);

      const res = await fetch(`${supabaseRestUrl}/internship_feedback?on_conflict=user_id,day`, {
        method: "POST",
        headers: {
          apikey: supabaseAnonKey,
          Authorization: `Bearer ${supabaseAnonKey}`,
          "Content-Type": "application/json",
          Prefer: "resolution=merge-duplicates",
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || "Failed to save");
      }

      message.success("Feedback saved to Supabase");
      setOpen(false);
    } catch (err) {
      console.log(err);

      message.error(err.error_description || err.message || "something went worng");
    }
    setSaving(false);
  };

  const daysValue = Form.useWatch("days", form) || [];
  const completedCount = (daysValue || []).filter((d) => d?.date && d?.activity_type && d?.performance && (d?.behaviors || [])?.length > 0 && d?.notes).length;

  // Filter users by name (frontend only)
  const teamOptions = useMemo(() => {
    const uniqueTeams = Array.from(new Set(users.map((u) => u.team).filter(Boolean)));
    return [{ label: "All teams", value: "" }, ...uniqueTeams.map((team) => ({ label: team, value: team }))];
  }, [users]);

  const filteredUsers = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    return users.filter((user) => {
      const matchesSearch = term ? user.name?.toLowerCase().includes(term) : true;
      const matchesTeam = teamFilter ? user.team === teamFilter : true;
      return matchesSearch && matchesTeam;
    });
  }, [users, searchTerm, teamFilter]);

  const renderUserCard = (user) => (
    <Card
      key={user.id}
      bordered={false}
      style={{
        borderRadius: 10,
        boxShadow: "0 3px 8px rgba(0,0,0,0.1)",
        overflow: "hidden",
        padding: 0,
        width: "280px",
        margin: "auto",
      }}
      bodyStyle={{ padding: 0 }}
    >
      <div style={{ textAlign: "center" }}>
        <Avatar
          shape="square"
          size={250}
          src={user.image}
          icon={<UserOutlined />}
          style={{
            margin: "5px 0 5px",
            border: "none",
          }}
        />
      </div>

      <div style={{ padding: "20px", backgroundColor: "#fafafa" }}>
        <div style={{ marginBottom: 8 }}>
          <Text strong style={{ margin: 0, textAlign: "left" }}>
            {user.name}
          </Text>
        </div>

        <Space direction="vertical" size={6} style={{ width: "100%", textAlign: "left", marginBottom: 4 }}>
          <Space size={8}>
            <PhoneOutlined style={{ color: "#8c8c8c", fontSize: 14 }} />
            <Text type="secondary" style={{ fontSize: 12, color: "#8c8c8c" }}>
              {user.phone || "N/A"}
            </Text>
          </Space>
          <Space size={8}>
            <MailOutlined style={{ color: "#8c8c8c", fontSize: 14 }} />
            <Text type="secondary" style={{ fontSize: 12, color: "#8c8c8c" }}>
              {user.email || "N/A"}
            </Text>
          </Space>
        </Space>
        <Button onClick={() => handleOpen(user)} type="link">
          Add Feedback <span style={{ marginLeft: 4 }}>+</span>
        </Button>
      </div>
    </Card>
  );

  const behaviorCardStyle = (checked) => ({
    width: "100%",
    padding: "6px 8px",
    borderRadius: 10,
    border: `1px solid ${checked ? "#8575f0" : "#e6e6eb"}`,
    background: checked ? "lightyellow" : "#FAFAFA",
    display: "flex",
    alignItems: "center",
    gap: 10,
  });

  const tabsItems = useMemo(
    () =>
      dayKeys.map((day, index) => {
        const dayBehaviors = daysValue?.[index]?.behaviors || [];
        return {
          key: String(day),
          label: `Day ${day}`,
          children: (
            <Space direction="vertical" style={{ width: "100%" }} size="large">
              <Row gutter={12}>
                <Col xs={24} md={12}>
                  <Form.Item name={["days", index, "date"]} label="Date" rules={[{ required: true, message: "Please select the date" }]}>
                    <DatePicker style={{ width: "100%" }} format="dddd, MMMM DD, YYYY" disabledDate={(current) => current && current > dayjs().endOf("day")} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name={["days", index, "activity_type"]} label="Activity Type" rules={[{ required: true, message: "Please choose activity type" }]}>
                    <Select placeholder="Select activity type..." options={activityTypeOptions} />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name={["days", index, "behaviors"]}
                label="What specific behaviors did you see? (select all that apply)"
                rules={[
                  {
                    required: true,
                    message: "Select at least one observed behavior",
                  },
                ]}
              >
                <Checkbox.Group style={{ width: "100%" }}>
                  <Row gutter={[12, 12]}>
                    {behaviorOptions.map((option) => {
                      const checked = dayBehaviors.includes(option.value);
                      return (
                        <Col xs={24} sm={24} lg={24} key={option.value}>
                          <Checkbox value={option.value}>
                            <div style={behaviorCardStyle(checked)}>{option.label}</div>
                          </Checkbox>
                        </Col>
                      );
                    })}
                  </Row>
                </Checkbox.Group>
              </Form.Item>

              <Form.Item
                name={["days", index, "performance"]}
                label="How was their performance in this specific session?"
                rules={[
                  {
                    required: true,
                    message: "Please rate their performance",
                  },
                ]}
              >
                <Select placeholder="Select performance rating..." options={performanceOptions} />
              </Form.Item>

              <Form.Item name={["days", index, "notes"]} label="Observation Note" rules={[{ required: true, message: "Please add an observation note" }]}>
                <TextArea rows={5} placeholder="Add concise observations" />
              </Form.Item>
            </Space>
          ),
        };
      }),
    [daysValue]
  );

  return (
    <div style={{ padding: 16 }}>
      <Space direction="vertical" style={{ width: "100%", marginBottom: 16, position: "relative" }} size="small">
        <Title level={2} style={{ margin: 0 }}>
          Internship Feedback
        </Title>
        <Text type="secondary">Mentors can open a trainee card to record day-wise feedback for all internship days.</Text>
      </Space>

      {loadingUsers && <LoadingOutlined spin style={{ position: "absolute", top: "40%", left: "50%", fontSize: "50px", color: "#971eff" }} />}

      <div style={{ marginBottom: 16 }}>
        <Space size="middle" wrap>
          <Input.Search
            placeholder="Search by name..."
            allowClear
            enterButton={<SearchOutlined />}
            size="large"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ minWidth: 280, maxWidth: 360 }}
          />
          <Select
            placeholder="Filter by team"
            options={teamOptions}
            size="large"
            value={teamFilter}
            onChange={(val) => setTeamFilter(val || "")}
            style={{ minWidth: 200 }}
            allowClear
          />
        </Space>
      </div>

      {!loadingUsers && filteredUsers.length === 0 && searchTerm && (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Text type="secondary">No users found matching "{searchTerm}"</Text>
        </div>
      )}

      <Row gutter={[0, 16]}>
        {filteredUsers.map((user) => (
          <Col xs={24} sm={12} md={8} lg={6} key={user.id}>
            {renderUserCard(user)}
          </Col>
        ))}
      </Row>

      <Modal
        open={open}
        title={null}
        width={980}
        onCancel={handleCancel}
        onOk={handleSubmit}
        okText="Save Feedback"
        cancelText="Cancel"
        destroyOnClose
        style={{ top: 24 }}
        // bodyStyle={{ padding: 0, background: "#f8f9fb" }}
        confirmLoading={saving}
      >
        {selectedUser && (
          <Form form={form} layout="vertical" initialValues={{ days: buildEmptyDays() }}>
            <div style={{ padding: 24, marginBottom: 24 }}>
              <Space align="center" size="middle">
                <Avatar size={64} src={selectedUser.image} icon={<UserOutlined />} />
                <div>
                  <Title level={4} style={{ margin: 0 }}>
                    {selectedUser.name}
                  </Title>
                  <Text type="secondary">10-Day Internship Feedback</Text>
                  <div style={{ marginTop: 8 }}>
                    <Space size="large">
                      <Space size={6}>
                        <PhoneOutlined />
                        <Text>{selectedUser.phone}</Text>
                      </Space>
                      <Space size={6}>
                        <MailOutlined />
                        <Text>{selectedUser.email}</Text>
                      </Space>
                    </Space>
                  </div>
                </div>
              </Space>
              {loadingRemote && (
                <div style={{ marginTop: 12 }}>
                  <Tag color="blue">Loading saved feedback…</Tag>
                </div>
              )}
              <Divider />
            </div>

            <div style={{ padding: "0 24px 16px 24px" }}>
              <Tabs activeKey={activeDay} onChange={setActiveDay} items={tabsItems} tabBarGutter={8} type="card" animated />
            </div>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default FeedbackDubai;
